import React, { useState, useEffect, useMemo } from "react";
import DatePicker from "react-datepicker";
import { Label } from "flowbite-react";
import CustomButton from '../../../components/common/CustomButton';
import FormInput from '../../../components/common/FormInput';
import { Drawer } from "../../../components/Modals";
import { formatDate } from "../../../helpers/data-format";
import { useJwt } from "../../../helpers/common";
import { useSelector } from "react-redux";
import { HiTrash } from "react-icons/hi";
import ConfirmDeleteModal from '../../../components/Modals/ConfirmDeleteModal';
import {useOrganizationService} from "../../../services";
import UnsavedChangesIndicator from '../../../components/common/UnsavedChangesIndicator';

interface SchedulesModalProps {
  isOpen: boolean;
  onClose: () => void;
  currentData: any;
  onSubmitSuccess?: () => void;
}

const SchedulesModal: React.FC<SchedulesModalProps> = ({ isOpen, onClose, currentData, onSubmitSuccess }) => {
  const { updateOrgSchedule, addOrgSchedule, deleteOrgSchedule } = useOrganizationService();
  const {
    orgScheduleLoading
  } = useSelector((state: any) => state.organization);
  const jwtData: any = useJwt();
  const org_id = jwtData.org_id;
  const [payload, setPayload] = useState<{ name: string; date: string | null; id?: string | number } | any>({ name: '', date: null });
  const [showDeleteModal, setShowDeleteModal] = useState(false);

  useEffect(() => {
    setPayload(currentData);
  }, [currentData]);

  const hasUnsavedChanges = useMemo(() => {
    if (!currentData?.id || !payload?.name) {
      return false;
    }

    const normalizeString = (val: any) => (val === null || val === undefined ? '' : String(val).trim());
    const normalizeDate = (val: any) => (val ? formatDate(val) : '');

    return (
      normalizeString(payload?.name) !== normalizeString(currentData?.name) ||
      normalizeDate(payload?.date) !== normalizeDate(currentData?.date)
    );
  }, [currentData, payload?.name, payload?.date]);

  const handleOnChnage = (e: any) => {
    const { name, value } = e.target;
    setPayload({ ...payload, [name]: value });
    
  };
  const handleOnDateChange = (date: any) => {
    const data = { ...payload, date };
    setPayload(data);
  };

  const isWeekday = (date: Date, org_id: string) => {
    const day = date.getDay();
    if (org_id && localStorage) {
      let workingDay: any = localStorage.getItem("workingDays") ? localStorage.getItem("workingDays") : "";
      let workingDays: any = workingDay ? JSON.parse(workingDay) : {};
      let orgWorkingDays: any = workingDays[org_id] ? workingDays[org_id] : [];
      if (orgWorkingDays.length) {
        const d = day === 0 ? 7 : day;
        return orgWorkingDays.includes(d);
      } else {
        return day !== 0 && day !== 6;
      }
    } else {
      return day !== 0 && day !== 6;
    }
  };

  const handleDelete = () => {
    setShowDeleteModal(true);
  };

  const handleConfirmDelete = async () => {
    if (!payload.id) return;
    await deleteOrgSchedule(org_id, { id: payload.id });
    setShowDeleteModal(false);
    onClose();
  };

  const onSubmit = () => {
    if (payload?.id) {
      updateOrgSchedule(org_id, { ...payload, date: formatDate(payload.date), type: "public" });
      setTimeout(() => {
        onClose();
        if (onSubmitSuccess) onSubmitSuccess();
      }, 1000);
    } else {
      addOrgSchedule(org_id, { ...payload, date: formatDate(payload.date), type: "public" });
      onClose();
      if (onSubmitSuccess) onSubmitSuccess();
    }
  };

  return (
    <Drawer isOpen={isOpen} onClose={onClose} position="right" size="42" minsize="42">
      <div className="relative h-full overflow-auto flex flex-col">
        {/* Header */}
        <div className="flex flex-col border-b bg-light-surfacePrimary border-gray-500 dark:border-gray-700 !p-4 !pl-6 !pt-5 dark:bg-tertiary-dark sticky top-0 z-40 shadow-lg rounded-b-2xl">
          <div className="flex  items-start justify-between mb-2">
            <div className="flex flex-col min-w-0 max-w-full">
              <div className="text-light-text-primary dark:text-white text-lg font-semibold font-['Inter'] leading-tight truncate pr-2">
                {currentData?.id ? 'Edit Holiday' : 'Add New Holiday'}
              </div>
              <div className="text-light-text-tertiary dark:text-white/60 text-sm mt-1">
                {currentData?.id ? 'Update the holiday details below.' : 'Fill in the details to add a new holiday.'}
              </div>
            </div>
            <div className="flex items-center gap-2">
              {currentData?.id && (
                <button
                  type="button"
                  className="text-red-600 hover:text-red-800 p-2 rounded-lg transition-colors duration-200"
                  title="Delete Holiday"
                  onClick={handleDelete}
                >
                  <HiTrash className="w-5 h-5" />
                </button>
              )}
              <button
                type="button"
                className="text-light-text-secondary hover:text-light-text-primary dark:text-white/70 dark:hover:text-white hover:bg-light-interactive-hover dark:hover:bg-white/10 p-2 rounded-lg transition-colors duration-200"
                onClick={onClose}>
                <svg className="w-5 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg">
                  <path fillRule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clipRule="evenodd"></path>
                </svg>
              </button>
            </div>
          </div>
        </div>
        {/* Body */}
        <div className="flex-1 flex flex-col px-16 py-8 bg-light-surfacePrimary dark:bg-tertiary-dark">
          <FormInput
            id="name"
            name="name"
            label="Name"
            value={payload?.name}
            onChange={handleOnChnage}
            placeholder="Name"
            required
            showAsterisk
            className="!bg-gray-100 dark:!bg-[#374151]"
          />
          <div className="w-full">
            <Label htmlFor="description" className='flex'>
              <span>Date</span>
              <span className="ml-1">*</span>
            </Label>
            <div className="mt-1 mb-2">
              <DatePicker
                id="start_date"
                className="block w-full p-2 !bg-gray-100 dark:!bg-[#374151] border border-gray-300 dark:border-gray-600 text-gray-900 dark:text-white rounded-md focus:ring-blue-500 focus:border-blue-500"
                selected={(payload?.date !== null && payload?.date !== undefined && payload?.date !== "None") ? new Date(payload?.date) : null}
                onChange={(date: any) => handleOnDateChange(date)}
                autoComplete='off'
                filterDate={(date: Date) => isWeekday(date, org_id)}
                placeholderText="Select date"
                required
              />
            </div>
          </div>
        </div>
        {/* Footer */}
        <div className="px-4 py-4 items-center justify-between flex gap-4 mt-auto border-t rounded-t-md border-gray-500 dark:border-gray-700 bg-light-surfacePrimary dark:bg-tertiary-dark shadow-lg">
          <CustomButton
            variant="cancel"
            onClick={onClose}
            className="hover:bg-light-interactive-hover dark:hover:bg-hover-dark transition-all duration-200"
          >
            Cancel
          </CustomButton>
          <div className="ml-auto flex items-center gap-3">
            {currentData?.id && (
              <UnsavedChangesIndicator hasUnsavedChanges={hasUnsavedChanges} />
            )}
            <CustomButton
              onClick={onSubmit}
              disabled={
                orgScheduleLoading
                || !payload?.name
                || !payload?.date
              }
            >
              Save
            </CustomButton>
          </div>
        </div>
      </div>
      <ConfirmDeleteModal
        showModal={showDeleteModal}
        setShowModal={setShowDeleteModal}
        handleConfirm={handleConfirmDelete}
        title="Delete Holiday"
        description={`Are you sure you want to delete "${payload?.name || 'this holiday'}"? This action cannot be undone.`}
        confirmText="Delete"
      />
    </Drawer>
  );
};

export default SchedulesModal; 